import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Button, Alert, IconButton, TextField,
  MenuItem, Chip, Divider, Tooltip, CircularProgress, Autocomplete
} from '@mui/material';
import { Add, Delete, Home, ReceiptLong, Send } from '@mui/icons-material';
import axios from 'axios';
import { useBankAccounts } from '../hooks/useBankAccounts';
import { fmt, sym } from '../utils/currency';

const API = process.env.REACT_APP_API_URL;
const emptyLine = () => ({ _key: Date.now() + Math.random(), learnerAdmNo: '', amount: '', learnerName: '', creditBalance: 0, outstanding: null, learnerObj: null });

const BatchReceiptForm = () => {
  const navigate = useNavigate();
  const { banks: bankAccounts } = useBankAccounts();

  const [date,          setDate]          = useState(new Date().toISOString().split('T')[0]);
  const [bankAccountNo, setBankAccountNo] = useState('');
  const [reference,     setReference]     = useState('');
  const [lines,         setLines]         = useState([emptyLine()]);
  const [error,         setError]         = useState('');
  const [posting,       setPosting]       = useState(false);
  const [result,        setResult]        = useState(null);
  const [allLearners,   setAllLearners]   = useState([]);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    axios.get(`${API}/api/learners`, { headers })
      .then(r => setAllLearners(r.data?.data || []))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLearnerInfo = async (key, admNo) => {
    if (!admNo) return;
    try {
      const [creditRes, balRes] = await Promise.allSettled([
        axios.get(`${API}/api/finance/learner-credits/${admNo}`, { headers }),
        axios.get(`${API}/api/finance/learner/${admNo}/balance`,  { headers }),
      ]);
      const creditBalance = creditRes.status === 'fulfilled' ? (creditRes.value.data.totalCredit || 0) : 0;
      const outstanding   = balRes.status   === 'fulfilled' ? (balRes.value.data.balance   || 0) : null;
      const autoAmount    = outstanding > 0 ? outstanding.toFixed(2) : '';
      setLines(ls => ls.map(l => l._key === key
        ? { ...l, creditBalance, outstanding, amount: l.amount || autoAmount }
        : l));
    } catch { /* ignore */ }
  };

  const handleLearnerChange = (key, learnerObj) => {
    if (!learnerObj) {
      setLines(ls => ls.map(l => l._key === key
        ? { ...l, learnerAdmNo: '', learnerName: '', learnerObj: null, creditBalance: 0, outstanding: null }
        : l));
      return;
    }
    setLines(ls => ls.map(l => l._key === key
      ? { ...l, learnerAdmNo: learnerObj.admNo, learnerName: learnerObj.name, learnerObj }
      : l));
    fetchLearnerInfo(key, learnerObj.admNo);
  };

  const addLine    = () => setLines(ls => [...ls, emptyLine()]);
  const removeLine = (key) => setLines(ls => ls.filter(l => l._key !== key));
  const updateLine = (key, field, value) =>
    setLines(ls => ls.map(l => l._key === key ? { ...l, [field]: value } : l));

  const totalReceived = lines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);

  const handlePost = async () => {
    if (!bankAccountNo) { setError('Bank account is required'); return; }
    const validLines = lines.filter(l => l.learnerAdmNo.trim() && parseFloat(l.amount) > 0);
    if (validLines.length === 0) { setError('At least one valid learner line is required'); return; }

    setPosting(true); setError('');
    try {
      const res = await axios.post(`${API}/api/finance/batch-receipts`, {
        date, bankAccountNo, reference,
        lines: validLines.map(l => ({ learnerAdmNo: l.learnerAdmNo.trim(), amount: parseFloat(l.amount) }))
      }, { headers });
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Posting failed');
    } finally { setPosting(false); }
  };

  if (result) {
    return (
      <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4, mb: 4 }}>
        <Card elevation={3}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <ReceiptLong color="success" />
              <Typography variant="h5" color="success.main">Batch Posted — {result.batchNo}</Typography>
            </Box>
            <Box display="flex" gap={3} mb={2}>
              <Box><Typography variant="caption" color="text.secondary">Total Received</Typography>
                <Typography variant="h6">{sym()} {fmt(result.totalReceived)}</Typography></Box>
              <Box><Typography variant="caption" color="text.secondary">Applied to Invoices</Typography>
                <Typography variant="h6" color="success.main">{sym()} {fmt(result.totalApplied)}</Typography></Box>
              {result.totalCredit > 0 && (
                <Box><Typography variant="caption" color="text.secondary">Credit Created (Advance)</Typography>
                  <Typography variant="h6" color="warning.main">{sym()} {fmt(result.totalCredit)}</Typography></Box>
              )}
            </Box>
            <Divider sx={{ mb: 2 }} />

            {result.lines.map((ln, i) => (
              <Box key={i} mb={1.5} p={1.5} bgcolor="grey.50" borderRadius={2}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                  <Typography variant="subtitle2">{ln.learnerAdmNo} — {ln.learnerName}</Typography>
                  <Box display="flex" gap={1}>
                    <Chip label={`Received ${sym()} ${fmt(ln.amountReceived)}`} size="small" />
                    <Chip label={`Applied ${sym()} ${fmt(ln.amountApplied)}`} size="small" color="success" variant="outlined" />
                    {ln.creditCreated > 0 && (
                      <Chip label={`Credit ${sym()} ${fmt(ln.creditCreated)}`} size="small" color="warning" variant="outlined" />
                    )}
                  </Box>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {ln.allocations.length} invoice(s) settled:&nbsp;
                  {ln.allocations.map(a => `${a.invoiceNo} (${fmt(a.amount)})`).join(' · ')}
                </Typography>
              </Box>
            ))}

            <Box mt={3} display="flex" gap={2}>
              <Button variant="outlined" onClick={() => navigate('/finance/batch-receipts')}>View All Batches</Button>
              <Button variant="contained" onClick={() => { setResult(null); setLines([emptyLine()]); }}>
                New Batch
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 960, mx: 'auto', mt: 4, mb: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <ReceiptLong color="primary" />
              <Typography variant="h5" color="primary">New Batch Payment Receipt</Typography>
            </Box>
            <Tooltip title="Back to list">
              <IconButton onClick={() => navigate('/finance/batch-receipts')}
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}>
                <Home />
              </IconButton>
            </Tooltip>
          </Box>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {/* Header */}
          <Box display="flex" gap={2} mb={3} flexWrap="wrap">
            <TextField label="Date *" type="date" size="small" value={date}
              onChange={e => setDate(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }} />
            <TextField select label="Bank Account *" size="small" value={bankAccountNo}
              onChange={e => setBankAccountNo(e.target.value)} sx={{ minWidth: 260 }}>
              <MenuItem value=""><em>Select bank account</em></MenuItem>
              {bankAccounts.map(b => (
                <MenuItem key={b.accountNo} value={b.accountNo}>
                  {b.accountNo} — {b.accountName} ({b.bankName})
                </MenuItem>
              ))}
            </TextField>
            <TextField label="Reference" size="small" value={reference}
              onChange={e => setReference(e.target.value)} sx={{ minWidth: 200 }}
              placeholder="e.g. Bank deposit slip no." />
          </Box>

          <Divider sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">Learner Lines</Typography>
          </Divider>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Select each learner and enter the lump-sum amount received. The system applies amounts to open invoices in charge item sequence order. Any surplus becomes an advance credit applied to the next invoice.
          </Typography>

          <Table size="small" sx={{ mb: 1 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ width: 280 }}><strong>Learner</strong></TableCell>
                <TableCell sx={{ width: 110 }}><strong>Adm No</strong></TableCell>
                <TableCell sx={{ width: 130 }}><strong>Outstanding ({sym()})</strong></TableCell>
                <TableCell sx={{ width: 110 }}><strong>Adv. Credit</strong></TableCell>
                <TableCell sx={{ width: 170 }} align="right"><strong>Amount Received ({sym()})</strong></TableCell>
                <TableCell sx={{ width: 40 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map(line => (
                <TableRow key={line._key}>
                  <TableCell>
                    <Autocomplete
                      size="small"
                      options={allLearners}
                      getOptionLabel={o => o ? `${o.name} (${o.admNo})` : ''}
                      isOptionEqualToValue={(o, v) => o.admNo === v?.admNo}
                      value={line.learnerObj}
                      onChange={(_, val) => handleLearnerChange(line._key, val)}
                      renderInput={params => (
                        <TextField {...params} placeholder="Search by name or Adm No" />
                      )}
                      filterOptions={(opts, { inputValue }) => {
                        const q = inputValue.toLowerCase();
                        return opts.filter(o =>
                          o.name?.toLowerCase().includes(q) ||
                          o.admNo?.toLowerCase().includes(q)
                        );
                      }}
                      noOptionsText="No learners found"
                      sx={{ minWidth: 260 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {line.learnerAdmNo || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {line.outstanding === null ? (
                      <Typography variant="caption" color="text.disabled">—</Typography>
                    ) : line.outstanding > 0 ? (
                      <Chip label={fmt(line.outstanding)} size="small" color="error" variant="outlined" />
                    ) : (
                      <Chip label="Cleared" size="small" color="success" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell>
                    {line.creditBalance > 0
                      ? <Chip label={fmt(line.creditBalance)} size="small" color="warning" variant="outlined" />
                      : <Typography variant="caption" color="text.disabled">—</Typography>}
                  </TableCell>
                  <TableCell>
                    <TextField size="small" type="number" value={line.amount} fullWidth
                      inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }}
                      onChange={e => updateLine(line._key, 'amount', e.target.value)} />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" color="error" onClick={() => removeLine(line._key)}
                      disabled={lines.length === 1}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}

              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell colSpan={4}><strong>Total</strong></TableCell>
                <TableCell align="right"><strong>{sym()} {fmt(totalReceived)}</strong></TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>

          <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
            <Button size="small" startIcon={<Add />} variant="outlined" onClick={addLine}>
              Add Learner
            </Button>
            <Button variant="contained" size="large"
              startIcon={posting ? <CircularProgress size={18} color="inherit" /> : <Send />}
              onClick={handlePost} disabled={posting}>
              {posting ? 'Posting…' : 'Post Batch Receipt'}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default BatchReceiptForm;
